import {
  type RankingInfo,
  rankItem,
  rankings,
} from "@tanstack/match-sorter-utils";
import type { FilterFn, Row } from "@tanstack/react-table";

import type { QueryResultColumn } from "~/utils/Types";

import type { MeqColumnDef, MeqRowData } from "./types";

/**
 * Filtering logic ported from the old (now-deleted) MRT-based wrapper, plus
 * the global-filter fuzzy-matching decision recorded during the spike
 * (plans/ins-1606/learnings.md, spike-meqtable-api entry).
 *
 * This module is internal to the MeqTable engine seam (like sortFns.ts and
 * useMeqTableInstance.ts, it may reference `@tanstack/react-table` /
 * `@tanstack/match-sorter-utils` types) — it is NOT part of the public
 * call-site-facing surface (that's types.ts).
 */

const isNumberLike = (value: unknown): boolean =>
  value !== null &&
  value !== undefined &&
  value !== "" &&
  !isNaN(Number(value));

export type NumberRangeFilterValue = [
  string | number | null,
  string | number | null,
];

/**
 * Number-range column filter fn — ported verbatim from the old wrapper's
 * `numberFilter`. Paired with `filterVariant: "range"` on a `MeqColumnDef`
 * and rendered via `NumberRangeFilter.tsx` when `hasColumnFilters` is on.
 */
export const numberRangeFilterFn = <TData extends MeqRowData>(
  row: Row<TData>,
  columnId: string,
  filterValue: NumberRangeFilterValue,
): boolean => {
  const value = row.getValue(columnId);
  const [min, max] = filterValue;
  const hasBound = (bound: string | number | null): boolean =>
    bound !== null && bound !== undefined && bound !== "";

  if ((hasBound(min) || hasBound(max)) && !isNumberLike(value)) {
    return false;
  }
  if (hasBound(min) && (!isNumberLike(min) || Number(min) > Number(value))) {
    return false;
  }
  if (hasBound(max) && (!isNumberLike(max) || Number(max) < Number(value))) {
    return false;
  }
  return true;
};

/**
 * Fuzzy filter fn — match-sorter parity (spike decision: FUZZY, not plain
 * "contains"). Registered both as the table's `globalFilterFn` and under the
 * "fuzzy" name in `filterFns` (useMeqTableInstance.ts) so a column def can
 * also opt in per-column via `filterFn: "fuzzy"`.
 *
 * Same mechanism TanStack's own fuzzy-filtering guide and the old MRT-based
 * wrapper's default `globalFilterFn: "fuzzy"` used:
 * `@tanstack/match-sorter-utils#rankItem` scores the cell value against the
 * filter text, and `addMeta` stashes the rank on
 * `row.columnFiltersMeta[columnId]` — populated by TanStack core itself for
 * any filter fn that calls `addMeta` (not MRT-specific machinery) — for
 * `rankGlobalFuzzy` to consume afterward.
 */
export const fuzzyFilterFn: FilterFn<MeqRowData> = (
  row,
  columnId,
  filterValue,
  addMeta,
) => {
  const itemRank = rankItem(row.getValue(columnId), filterValue, {
    threshold: rankings.MATCHES,
  });
  addMeta(itemRank);
  return itemRank.passed;
};
fuzzyFilterFn.autoRemove = (value: unknown) => !value;

/**
 * Ranks two rows by their best-matching column's fuzzy score — ported from
 * the old MRT-based wrapper's `rankGlobalFuzzy` — so the closest global-filter
 * matches surface first, matching the old Table's default search behavior
 * (the old wrapper's `enableGlobalFilterRankedResults` defaulted to `true`). Only
 * meaningful when no explicit column sort is active; see
 * useMeqTableInstance.ts's ranked row-model wiring for where this is
 * actually invoked.
 */
export const rankGlobalFuzzy = <TData extends MeqRowData>(
  rowA: Pick<Row<TData>, "columnFiltersMeta">,
  rowB: Pick<Row<TData>, "columnFiltersMeta">,
): number => {
  const bestRank = (row: Pick<Row<TData>, "columnFiltersMeta">): number =>
    Math.max(
      0,
      ...Object.values(row.columnFiltersMeta).map(
        (meta) => (meta as RankingInfo | undefined)?.rank ?? 0,
      ),
    );
  return bestRank(rowB) - bestRank(rowA);
};

/**
 * Old-wrapper parity: auto-derive `filterFn`/`filterVariant` for number
 * columns from `columnDataTypes` (ported from the old wrapper's
 * `optimisedColumns` mapping). An explicit
 * `filterFn`/`filterVariant` already set on the column def always wins
 * (matches the old wrapper's `{ ...derived, ...original }` merge order).
 */
export const withDerivedNumberFilters = <TData extends MeqRowData>(
  columns: MeqColumnDef<TData>[],
  columnDataTypes: QueryResultColumn[] | undefined,
): MeqColumnDef<TData>[] => {
  if (!columnDataTypes?.length) {
    return columns;
  }
  return columns.map((column) => {
    const columnDataType = columnDataTypes.find(
      (dataType) => dataType.name === column.accessorKey,
    );
    if (columnDataType?.type !== "number") {
      return column;
    }
    return { filterFn: "number", filterVariant: "range", ...column };
  });
};
